import { isbot } from 'isbot';
import Maybe from 'true-myth/maybe';

import { COMET_1, getCurrentEnvForClientSide, PRODUCTION, STAGING } from './client';

declare const __DEV__: boolean | undefined;
import { getNamespace } from './server';

declare global {
  interface Window {
    Cypress?: unknown;
  }
}

export { PRODUCTION_ENV } from './server';

export function isProduction() {
  if (typeof window !== 'undefined') {
    return getCurrentEnvForClientSide() === PRODUCTION;
  }
  return getNamespace() === 'production';
}

/**
 * Page frame service for the current environment: prod page frame in production, QA page frame everywhere else.
 * Read at runtime so a single build fetches QA page frame (and its QA analytics config) when deployed to QA.
 */
export function getPageFrameServiceHost() {
  return isProduction() ? 'https://www.zillow.com/pfs' : 'https://www.qa.zillow.net/pfs';
}

let isPrinted = false;

function printDevMessage() {
  if (!isPrinted) {
    // eslint-disable-next-line no-console
    console.info('We are in dev mode, metrics data will not be sent');
    isPrinted = true;
  }
}

function printProdMessage() {
  if (!isPrinted) {
    // console.info, not console.error: this is informational, and every
    // console.error on a healthy prod page fails Lighthouse's
    // errors-in-console audit (Best Practices score).
    // eslint-disable-next-line no-console
    console.info(
      'We are in production mode, metrics data will be sent. If you are running a dev server something is' +
        ' not correct.',
    );
    isPrinted = true;
  }
}

export function isDev() {
  // bots
  if (typeof window !== 'undefined' && isBrowserBot(window.navigator?.userAgent ?? '')) {
    return true;
  }
  if (isCypress()) {
    return true;
  }

  // docker build/validate
  if (typeof process !== 'undefined' && (process.env?.RJS_DOCKER_BUILD || process.env?.RJS_DOCKER_VALIDATE)) {
    printDevMessage();
    return true;
  }

  // next js dev mode
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    printDevMessage();
    return true;
  }

  // webpack dev mode __DEV__
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    printDevMessage();
    return true;
  }

  const isClientSide = typeof window !== 'undefined';
  const isClientSideEnabled =
    isClientSide &&
    (getCurrentEnvForClientSide() === PRODUCTION ||
      getCurrentEnvForClientSide() === COMET_1 ||
      getCurrentEnvForClientSide() === STAGING);

  if (isClientSide && !isClientSideEnabled) {
    printDevMessage();
    return true;
  }

  if ((typeof process !== 'undefined' && process.env?.RJS !== 'true') || isClientSideEnabled) {
    printProdMessage();
  }

  return false;
}

function isCypress() {
  if (typeof window !== 'undefined' && !!window.Cypress) {
    return true;
  }
  return false;
}

export function isTest() {
  if (isCypress()) {
    return true;
  }

  return typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
}

export function isBrowserBot(userAgent: string) {
  return isbot(userAgent);
}

export function injectAppNameHeader(headers?: Record<string, string>, appName?: string) {
  const newHeaders = { ...headers };
  if (appName) {
    newHeaders['x-datarouter-account-name'] = appName;
  }
  return newHeaders;
}

// required to consolidate with java
const envMappings: Record<string, string> = {
  'comet-1': 'comet1',
  'ttest-2': 'ttest2',
  'ztest-1': 'ztest1',
  'ztest-2': 'ztest2',
  'ztest-3': 'ztest3',
  'ztest-4': 'ztest4',
  'ztest-5': 'ztest5',
  'hptest-3': 'hptest3',
  'ptest-1': 'ptest1',
  'www.qa.zillow.net': 'comet1',
  'comet1.testpads.net': 'comet1',
  'www.ra1.zillow.net': 'ztest1',
  'www.ra2.zillow.net': 'ztest2',
  'www.ra3.zillow.net': 'ztest3',
  'www.ra4.zillow.net': 'ztest4',
  'www.ra5.zillow.net': 'ztest5',
};

export function getEnvironmentOverride(): Maybe<string> {
  const isClientSide = typeof window !== 'undefined';
  let env: string | undefined = undefined;
  if (isClientSide) {
    // note: jsdom doesn't fully shim window
    try {
      if (getCurrentEnvForClientSide() === COMET_1 || getCurrentEnvForClientSide() === STAGING) {
        env = window.location.hostname;
      }
    } catch {
      // no-op
    }
  } else {
    const namespace = getNamespace();
    if (namespace === 'kubernetes_staging') {
      env = process.env?.ZG_ENV ?? 'kubernetes_staging';
    }
  }

  // consolidate environments with java
  if (env && envMappings[env]) {
    env = envMappings[env];
  }

  return Maybe.of(env);
}
